import { IFaceted, IFacetMapping, IFacet } from '../Facet';
import { getItemsOperation } from '../../operations/getItemsOperation';
import { IItemCacheState, getCacheItem } from '../../store/reducers/itemCacheReducer';
import { IItemCache } from '../IItemCacheObserver';
import { DemandResolution, resolvedFacet } from '../../facets/DemandFacets';
import { mapObject, areShallowEqual } from '../../utilities/objects';
import { preferredKeyFacetFacet, isOperationHandlerRegisteredFacet, supportedFacetsFacet } from '../../facets/OperationFacets';
import { KeyFacet } from '../KeyFacet';
import { ComputedItemFacet } from './ComputedItemFacet';
import { IControlFaceted } from '../../components/decorators/withControlHandler';
import { ControlFacet, controlTypeKeyFacet, NO_CONTROL_HANDLERS } from '../controls/ControlFacet';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { getOperationTypeKey, isOperationTypeKey } from '../../store/reducers/operationMapReducer';
import { is } from '@ms/utilities-objects/lib/is';
import { IAction } from '../../store/actions/createActionCreator';
import { createMemoizer } from '../../utilities/Memoizer';
import { bindItemCacheSelectorToProps } from './bindItemCacheSelectorToProps';
import { IItemCacheEvaluator, IItemCacheEvaluation } from './ItemCacheEvaluator';
import { excludeResolvedDemands } from './excludeResolvedDemands';

const ERROR_ITEM_CACHE_DISPOSED =
  'The IItemCache instance may only be used within an invocation of `mapItemCacheToProps` ' +
  'passed to`connectToItemCache`.';

const DO_NOT_DEMAND: { [facetId: string]: boolean; } = {
  [preferredKeyFacetFacet.id]: true,
  [resolvedFacet.id]: true,
  [supportedFacetsFacet.id]: true,
  [isOperationHandlerRegisteredFacet.id]: true
};

const DO_NOT_DEPEND: { [facetId: string]: boolean; } = {
  [resolvedFacet.id]: true,
  [supportedFacetsFacet.id]: true
};

interface IItemEntry {
  // Debug and reference.
  item: IFaceted | undefined;

  // Reported and rolled-up values.
  demands: IFaceted<boolean> | undefined;
  dependencies: IFaceted<{} | undefined> | undefined;
  resolutions: IFaceted<DemandResolution> | undefined;

  // Memoizers.
  computedValues: IFaceted<{} | undefined>;
  controls: IFaceted<{} | undefined>;
  computedResolutions: IFaceted<boolean>;
}

/**
 * @internal
 */
export const memoizeGetCacheItem = createMemoizer((state: IItemCacheState) => {
  const items: {
    [itemKey: string]: IFaceted | undefined;
  } = {};

  return (itemKey: string) => {
    return (itemKey in items) ?
      items[itemKey] :
      (items[itemKey] = getCacheItem(state, itemKey));
  };
});

const memoizeGetOperationTypeKey = createMemoizer((keyFacet: KeyFacet<{}>) => {
  return getOperationTypeKey({ operationTypeFacet: getItemsOperation, keyFacet });
});

const memoizeGetControlTypeKey = createMemoizer((controlFacet: ControlFacet<{}, {}>) => {
  return createMemoizer((keyFacet: KeyFacet<{}>) => {
    return ControlFacet.getControlTypeKey({
      keyFacet: keyFacet,
      controlTypeFacet: controlFacet
    });
  });
});

const memoizeGetKeyFacet = createMemoizer((state: IItemCacheState) => {
  // Consming the current state so there is an object to bind to for garbage collection.
  const items: {
    [itemKey: string]: KeyFacet<{}>;
  } = {};

  return (itemKey: string) => {
    return (itemKey in items) ?
      items[itemKey] :
      (items[itemKey] = KeyFacet.get(itemKey));
  };
});

/**
 * @internal
 */
export interface IItemCacheDependencies {
  [itemKey: string]: IFaceted<{} | undefined>;
}

/**
 * @internal
 */
export interface IItemCacheDemands {
  [itemKey: string]: typeof getItemsOperation.type;
}

/**
 * @internal
 */
export interface IItemCacheParams {
  /**
   * The current state from which this observer will pull items and track dependencies.
   */
  state: IItemCacheState;
  /**
   * Previous dependencies against which to compare the current state for changes.
   */
  previousDependencies?: IItemCacheDependencies;
  /**
   * Previous demands for debouncing output.
   */
  previousDemands?: IItemCacheDemands;
  /**
   * Control handler overrides to use when resolving handlers.
   */
  controlHandlers?: IControlFaceted;
  /**
   * The store dispatcher that the observer will provide to selectors.
   */
  dispatch: IItemCacheDispatch;
  /**
   * An object to use to tag as the owner for the current item cache.
   * Can be used during debugging to set conditional breakpoints to capture usage
   * of item cache selectors for specific components.
   */
  owner?: {};
  /**
   * An evaluator to use when evaluating sub-selectors.
   */
  evaluator?: IItemCacheEvaluator;
}

/**
 * Implementation of the component which provides access to the item cache,
 * while also recording what is demanded along with the state of all cache
 * entries at the time they are demanded.
 * @internal
 */
export class ItemCacheObserver implements IItemCache {
  protected _owner: {};

  private _state: IItemCacheState;
  private _getCacheItem: (itemKey: string) => IFaceted | undefined;
  private _getKeyFacet: (itemKey: string) => KeyFacet<{}>;
  private _previousDependencies: IItemCacheDependencies | undefined;
  private _previousDemands: IItemCacheDemands | undefined;
  private _controlHandlers: IControlFaceted;
  private _dispatch: IItemCacheDispatch;
  private _evaluator: IItemCacheEvaluator | undefined;

  /**
   * Track disposal on an isolated object.
   * This prevents the 'dispatch' function from capturing the entire
   * ItemCacheObserver in its closure, which could cause a memory leak.
   */
  private _scope: {
    isDisposed?: boolean | undefined;
  };

  private _entries: {
    [itemKey: string]: IItemEntry;
  };

  constructor(params: IItemCacheParams) {
    const {
      dispatch,
      state
    } = params;

    const scope = this._scope = { isDisposed: false };

    this._state = state;
    this._getCacheItem = memoizeGetCacheItem(state);
    this._getKeyFacet = memoizeGetKeyFacet(state);
    this._previousDemands = params.previousDemands;
    this._previousDependencies = params.previousDependencies;
    this._controlHandlers = params.controlHandlers || NO_CONTROL_HANDLERS;
    this._owner = params.owner || {};
    this._evaluator = params.evaluator;

    this._entries = {};

    this._dispatch = <T extends IAction>(action: T) => {
      if (!scope.isDisposed) {
        throw new Error(
          'Attempted to invoke the store dispatch within a callback to an IItemCache. ' +
          'This could disrupt the ability to track dependencies and cause unwanted cascading updates. ' +
          'Instead, hold onto dispatch in a local variable and invoke it in an asynchronous callback, such as an event handler, ' +
          'or simply return it from the selector before using it.');
      }

      return dispatch(action);
    };
  }

  public get dispatch(): IItemCacheDispatch {
    if (this._scope.isDisposed) {
      throw new Error(
        'Attempted to access the store dispatch on an ItemCache outside of its completed callback. ' +
        'This can cause a memory leak because an ItemCache holds onto stale store state. ' +
        'To obtain the store dispatch, access it during the callback and store it in a local variable.');
    }

    return this._dispatch;
  }

  /**
   * Evaluates an item cache selector against this observer and returns its result,
   * recording all demands and dependencies.
   */
  public evaluate<TResult>(selector: (itemCache: IItemCache) => TResult): {
    result: TResult;
  } {
    return {
      result: selector(this)
    };
  }

  public demandItem<TFacets extends object>(
    mapping: IFacetMapping<TFacets>,
    itemKey: string | undefined): Partial<TFacets> {

    if (this._scope.isDisposed) {
      throw new Error(ERROR_ITEM_CACHE_DISPOSED);
    }

    const facets = {} as Partial<TFacets>;

    for (const name of (<(keyof IFacetMapping<TFacets>)[]>Object.keys(mapping))) {
      const facet = mapping[name];

      if (!facet) {
        throw new Error(
          `Mapping '${name}' was not passed a valid Facet or IFacet instance in demandItem.`
        );
      }

      facets[name] = this.demandItemFacet(facet, itemKey);
    }

    return facets;
  }

  public demandItemFacet<TValue>(facet: IFacet<TValue>, itemKey: string | undefined): TValue | undefined {
    if (this._scope.isDisposed) {
      throw new Error(ERROR_ITEM_CACHE_DISPOSED);
    }

    const entry = this._addEntry(itemKey);

    if (facet instanceof ControlFacet && !controlTypeKeyFacet.matches(itemKey)) {
      return this._addControl(entry, itemKey, facet);
    }

    if (facet instanceof ComputedItemFacet) {
      return this._addComputedValue(entry, itemKey, facet);
    }

    if (!itemKey) {
      return undefined;
    }

    if (!DO_NOT_DEMAND[facet.id]) {
      // The resolved facet cannot itself be resolved.
      this._addResolution(entry, facet);
    }

    const value = this._addValue(entry, itemKey, facet);

    if (!value && (facet instanceof KeyFacet)) {
      // If the facet is a KeyFacet, then do the caller a favor and parse the input key if possible.
      // This can assist in making a decision while waiting for the real item to resolve.
      if (facet.matches(itemKey)) {
        return facet.deserialize(itemKey);
      }
    }

    return value;
  }

  public isItemFacetResolved<TValue>(facet: IFacet<TValue>, itemKey: string | undefined): boolean {
    if (this._scope.isDisposed) {
      throw new Error(ERROR_ITEM_CACHE_DISPOSED);
    }

    if (!itemKey) {
      return false;
    }

    if (DO_NOT_DEMAND[facet.id]) {
      return false;
    }

    const entry = this._addEntry(itemKey);

    if (facet instanceof ComputedItemFacet) {
      return this._addComputedResolution(entry, itemKey, facet);
    }

    const demandResolution = this._addResolution(entry, facet);

    return demandResolution === DemandResolution.resolved;
  }

  public haveDependenciesChanged(): boolean {
    const previousDependencies = this._previousDependencies;

    if (!previousDependencies) {
      return true;
    }

    // This function can't use a general deep-comparer because facets should not be deep-compared, with
    // exception of resolvedFacet.
    for (const itemKey in previousDependencies) {
      if (Object.prototype.hasOwnProperty.call(previousDependencies, itemKey)) {
        const previousItem = previousDependencies[itemKey];

        // Memoize accesses from the cache for performance, since it reduces key-mapping work.
        const item = this._getCacheItem(itemKey);

        for (const facetId in previousItem) {
          if (Object.prototype.hasOwnProperty.call(previousItem, facetId)) {
            if (facetId === resolvedFacet.id) {
              const previousResolved = resolvedFacet.unpack(previousItem);
              const resolved = item && resolvedFacet.unpack(item);

              const previousDemands = previousResolved && previousResolved.demands;
              const demands = resolved && resolved.demands;

              if (previousDemands) {
                for (const resolvedFacetId in previousDemands) {
                  if (Object.prototype.hasOwnProperty.call(previousDemands, resolvedFacetId)) {
                    const previousResolution = previousDemands[resolvedFacetId];
                    const resolution = demands && demands[resolvedFacetId];

                    if (normalizeDemandResolution(previousResolution) !== normalizeDemandResolution(resolution)) {
                      // Instead of returning true, return diagnostics about what changed.
                      // This aids debuggability because it will reveal what triggered a call back to a selector function
                      // when walking the stack.
                      return {
                        [itemKey]: {
                          [facetId]: {
                            demands: {
                              [resolvedFacetId]: {
                                previous: previousResolution,
                                current: resolution
                              }
                            }
                          }
                        }
                      } as {} as boolean;
                    }
                  }
                }
              }
            } else {
              const previousFacet = previousItem[facetId];
              const facet = item && item[facetId];

              if (!is(previousFacet, facet)) {
                // Instead of returning true, return diagnostics about what changed.
                // This aids debuggability because it will reveal what triggered a call back to a selector function
                // when walking the stack.
                return {
                  [itemKey]: {
                    [facetId]: {
                      previous: previousFacet,
                      current: facet
                    }
                  }
                } as {} as boolean;
              }
            }
          }
        }
      }
    }

    return false;
  }

  /**
   * Marks this item cache observer as disposed so callers can't access it asynchronously.
   */
  public dispose(): void {
    this._scope.isDisposed = true;
  }

  /**
   * Gets the current snapshot of demands from this item cache observer,
   * if they have differed from previous demands.
   *
   * The goal here is to track which facets the caller has demanded, and
   * emit the payload for the getItems request.
   */
  public getDemands(): IItemCacheDemands {
    const demands = mapObject(
      this._entries,
      (entry: IItemEntry, itemKey: string | number | symbol): typeof getItemsOperation.type => {
        const { demands: entryDemands } = entry;

        if (!entryDemands) {
          return { demands: {} as IFaceted<boolean> };
        }

        return {
          demands: mapObject(entryDemands, (value: boolean | undefined, facetId: string | number | symbol) => {
            return true;
          }) as IFaceted<boolean>
        };
      },
      (itemKey: string | number | symbol, entry: IItemEntry): string | undefined => {
        if (!entry.demands) {
          return;
        }

        return itemKey as string;
      }
    );

    const isDirty = !areShallowEqual(demands, this._previousDemands, (left: {
      demands: IFaceted<boolean>
    }, right: {
      demands: IFaceted<boolean>;
    }) => areShallowEqual(left.demands, right.demands));

    return isDirty || !this._previousDemands ? demands : this._previousDemands;
  }

  /**
   * Gets the current snapshot of dependencies from this item cache observer,
   * if they have differed from the previous state.
   *
   * The goal here is to to produce a mapping of all referenced
   * item keys to the current values of demanded facets,
   * along with the current resolution states of each referenced facet.
   * At the same time, compare the result against the previous dependencies
   * to determine if the previous dependencies may be reused.
   */
  public getDependencies(): IItemCacheDependencies {
    const dependencies = mapObject(
      this._entries,
      (entry: IItemEntry, itemKey: string | number | symbol): IFaceted => {
        const {
          dependencies: itemDependencies,
          resolutions: itemResolutions
        } = entry;

        return {
          ...itemDependencies || {},
          ...resolvedFacet.pack({
            demands: itemResolutions || {} as IFaceted<DemandResolution>
          })
        } as IFaceted;
      },
      (itemKey: string | number | symbol, entry: IItemEntry): string | undefined => {
        if (!itemKey || !entry.dependencies && !entry.resolutions) {
          return;
        }

        return itemKey as string;
      }
    );

    const isDirty = !areShallowEqual(
      dependencies,
      this._previousDependencies,
      (left: {}, right: {}, itemKey: string) => {
        const {
          [resolvedFacet.id]: {
            demands: leftDemands
          },
          ...leftFacets
        } = left as IFaceted<{ demands: IFaceted }>;

        const {
          [resolvedFacet.id]: {
            demands: rightDemands
          },
          ...rightFacets
        } = right as IFaceted<{ demands: IFaceted }>;

        return areShallowEqual(leftDemands, rightDemands) && areShallowEqual(leftFacets, rightFacets);
      });

    return isDirty || !this._previousDependencies ? dependencies : this._previousDependencies;
  }

  private _addEntry(itemKey: string | undefined): IItemEntry {
    const entry = this._entries[itemKey || ''] || (this._entries[itemKey || ''] = {
      item: itemKey && this._getCacheItem(itemKey) || undefined,
      computedResolutions: {} as IFaceted<boolean>,
      computedValues: {} as IFaceted,
      controls: {} as IFaceted,
      demands: undefined,
      dependencies: undefined,
      resolutions: undefined
    });

    return entry;
  }

  /**
   * Adds a dependency on a computed facet.
   */
  private _addComputedValue<TValue>(
    entry: IItemEntry,
    itemKey: string | undefined,
    facet: ComputedItemFacet<TValue>
  ): TValue | undefined {
    const computations = entry.computedValues;

    const facetId = facet.id;

    if (facetId in computations) {
      // This computation has already been seen and its dependencies recorded.
      return computations[facetId] as TValue;
    }

    const evaluation = this._addComputation(itemKey, facet);

    if (evaluation) {
      const {
        result,
        demands,
        dependencies
      } = evaluation;

      this._mergeDemands(demands);
      this._mergeDependencies(dependencies);

      computations[facetId] = result;

      return result;
    } else {
      // Without an item cache evaluator, the computed facet's selector should simply be evaluated in-line
      // as though it were part of the current selector.

      const selector = bindItemCacheSelectorToProps(facet.compute, {
        itemKey
      });

      const {
        result: value
      } = this.evaluate(selector);

      computations[facetId] = value;

      return value;
    }
  }

  /**
   * Adds a dependency on a computed facet.
   */
  private _addComputedResolution<TValue>(
    entry: IItemEntry,
    itemKey: string | undefined,
    facet: ComputedItemFacet<TValue>
  ): boolean {
    const resolutions = entry.computedResolutions;

    const facetId = facet.id;

    if (facetId in resolutions) {
      // This computation has already been seen and its dependencies recorded.
      return resolutions[facetId];
    }

    const evaluation = this._addComputation(itemKey, facet);

    if (evaluation) {
      const {
        demands,
        dependencies
      } = evaluation;

      const isResolved = Object.keys(excludeResolvedDemands(this, {
        items: demands
      }).items).length === 0;

      this._mergeDependencies(dependencies);

      resolutions[facetId] = isResolved;

      return isResolved;
    } else {
      // Without the ItemCacheEvaluator, the dependencies and demands of a computed facet cannot be
      // determined in isolation, to return `false` to avoid callers being able to take a dependency
      // on an unverified values.

      return false;
    }
  }

  private _addComputation<TValue>(
    itemKey: string | undefined,
    facet: ComputedItemFacet<TValue>
  ): IItemCacheEvaluation<TValue | undefined> | undefined {
    const evaluator = this._evaluator;

    if (evaluator) {
      const {
        selector,
        dependencies: computedDependencies
      } = evaluator.getComputedFacetSelector({
        computedItemFacet: facet,
        itemKey,
        state: this._state
      });

      this._mergeDependencies(computedDependencies);

      return evaluator.evaluate({
        controlHandlers: this._controlHandlers,
        owner: this._owner,
        selector,
        state: this._state,
        suspender: selector,
      });
    }
  }

  /**
   * Adds a dependency on a control handler accessed by the item cache selector.
   */
  private _addControl<TValue>(
    entry: IItemEntry,
    itemKey: string | undefined,
    facet: ControlFacet<{}, {}>): TValue | undefined {
    const controls = entry.controls;

    const facetId = facet.id;

    if (facetId in controls) {
      // This control has already been composed for the given item key.
      return controls[facetId] as TValue;
    }

    const keyFacet = itemKey && this._addPreferredKeyFacet(entry, itemKey);

    const controlTypeKey = keyFacet && memoizeGetControlTypeKey(facet)(keyFacet);

    const controlRegistryEntry = controlTypeKey && this._addEntry(controlTypeKey);

    const controlHandler = controlRegistryEntry && controlTypeKey &&
      this._addValue(controlRegistryEntry, controlTypeKey, facet) || undefined;

    const propsControlHandler = this._controlHandlers && facet.unpack(this._controlHandlers);

    const composedHandler = facet.compose(controlHandler, propsControlHandler) as {} as TValue | undefined;

    controls[facetId] = composedHandler;

    return composedHandler;
  }

  /**
   * Adds a dependency and demand on a facet value.
   */
  private _addValue<TValue>(entry: IItemEntry, itemKey: string, facet: IFacet<TValue>): TValue | undefined {
    const facetId = facet.id;

    const item = entry.item;

    const value = item && item[facetId] as TValue;

    if (!isOperationTypeKey(itemKey)) {
      if (!DO_NOT_DEPEND[facetId]) {
        registerDependency(entry, facetId, value);
      }

      if (!DO_NOT_DEMAND[facetId]) {
        const hasNotDemandedItem = registerDemand(entry, facetId);

        if (hasNotDemandedItem) {
          // Also add a dependency on the getItems operation handler.
          this._addOperationHandler(entry, itemKey);
        }
      }
    }

    return value;
  }

  /**
   * For an item, ensures that a dependency is always set up for its assocated preferred `KeyFacet`.
   */
  private _addPreferredKeyFacet(entry: IItemEntry, itemKey: string): KeyFacet<{}> {
    const item = entry.item;

    const preferredKeyFacet = item && item[preferredKeyFacetFacet.id] as KeyFacet<{}>;

    registerDependency(entry, preferredKeyFacetFacet.id, preferredKeyFacet);

    const preferredKey = preferredKeyFacet && item && item[preferredKeyFacet.id];

    if (preferredKeyFacet) {
      registerDependency(entry, preferredKeyFacet.id, preferredKey);
    }

    return preferredKeyFacet || this._getKeyFacet(itemKey);
  }

  /**
   * For an item, ensures that the operation handler for the associated `getItemsOperation` queue
   * is tracked as a dependency. If a handler is registered, the selector will need to be evaluated
   * again so demands may be re-issued.
   */
  private _addOperationHandler(entry: IItemEntry, itemKey: string): void {
    // Add a dependency on the preferred KeyFacet and key for the item.
    const keyFacet = this._addPreferredKeyFacet(entry, itemKey);

    const operationTypeKey = memoizeGetOperationTypeKey(keyFacet);

    const operationEntry = this._addEntry(operationTypeKey);

    const operationItem = operationEntry.item;

    const isOperationHandlerRegistered = operationItem && operationItem[isOperationHandlerRegisteredFacet.id];

    registerDependency(operationEntry, isOperationHandlerRegisteredFacet.id, isOperationHandlerRegistered);
  }

  /**
   * Adds a dependency on a facet resolution state.
   */
  private _addResolution(entry: IItemEntry, facet: IFacet<{}>): DemandResolution | undefined {
    const item = entry.item;

    const resolved = item && resolvedFacet.unpack(item);

    const demands = resolved && resolved.demands;

    const facetId = facet.id;

    const resolution = demands && demands[facetId];

    registerResolution(entry, facetId, resolution);

    return resolution;
  }

  private _mergeDemands(demands: IItemCacheDemands): void {
    for (const itemKey of Object.keys(demands)) {
      const entry = this._addEntry(itemKey);

      const { demands: itemDemands } = demands[itemKey];

      for (const id of Object.keys(itemDemands)) {
        registerDemand(entry, id);
      }
    }
  }

  private _mergeDependencies(dependencies: IItemCacheDependencies): void {
    for (const itemKey of Object.keys(dependencies)) {
      const entry = this._addEntry(itemKey);

      const facets = dependencies[itemKey];

      for (const id of Object.keys(facets)) {
        const value = facets[id];

        if (id === resolvedFacet.id) {
          const resolutions = value as typeof resolvedFacet.type | undefined;

          if (resolutions) {
            const { demands } = resolutions;

            for (const rid of Object.keys(demands)) {
              const resolution = demands[rid];

              registerResolution(entry, rid, resolution);
            }
          }
        } else {
          registerDependency(entry, id, value);
        }
      }
    }
  }
}

function registerDependency(entry: IItemEntry, facetId: string, value: {} | undefined): void {
  const facets: Partial<IFaceted> = entry.dependencies || (entry.dependencies = {} as IFaceted);

  facets[facetId] = value;
}

function registerResolution(entry: IItemEntry, facetId: string, resolution: DemandResolution | undefined): void {
  const resolutions = entry.resolutions || (entry.resolutions = {} as IFaceted<DemandResolution>);

  resolutions[facetId] = normalizeDemandResolution(resolution);
}

function registerDemand(entry: IItemEntry, facetId: string): boolean {
  const existingDemands = entry.demands;

  const demands = existingDemands || (entry.demands = {} as IFaceted<boolean>);

  demands[facetId] = true;

  return !existingDemands;
}

/**
 * Since the only resolution states which matter for cache refreshes are 'resolved' and 'unresolved',
 * this function 'normalizes' a demand into only one of those two states.
 */
function normalizeDemandResolution(demandResolution?: DemandResolution): DemandResolution {
  return demandResolution === DemandResolution.resolved ? DemandResolution.resolved : DemandResolution.unresolved;
}
